/**
 * R2 URL helpers — the CLIENT-SAFE half of `lib/r2.ts`.
 *
 * ## Why this file exists
 *
 * `lib/r2.ts` statically imports `getCloudflareContext` from
 * `@opennextjs/cloudflare`. Six `"use client"` components imported it —
 * including `AnnouncementBar` (mounted from `Header`, i.e. the shared storefront
 * layout shell, so EVERY storefront page) and `ProductDetailAddToCart` (every
 * PDP) — which dragged the Cloudflare adapter into the browser bundle for
 * helpers that are pure string manipulation. That is cold-start parse CPU and
 * download weight the browser can never use: the adapter's context only exists
 * inside a Worker request.
 *
 * This module therefore has **ZERO imports**. Nothing here can transitively
 * reach `@opennextjs/cloudflare`, `next/headers`, `node:*` or a Cloudflare
 * binding, which is the property that makes it safe to import from a
 * `"use client"` file (see `.claude/rules/code-style.md` — a server-only import
 * reaching a client component breaks `next build` in a way neither
 * `tsc --noEmit` nor the unit suite can see). Keep it that way: **do not add an
 * import to this file.**
 *
 * ## Why reading `process.env` alone is not a behaviour change
 *
 * `readR2BaseFromEnv()` deliberately consults `process.env.R2_PUBLIC_URL` and
 * nothing else, yet resolves to the same value `lib/r2.ts` does on every
 * runtime we ship:
 *
 * - **Workers (production SSR):** `@opennextjs/cloudflare`'s worker init calls
 *   `populateProcessEnv(url, env)` and copies every string entry of the
 *   Cloudflare `env` — which includes the `wrangler.jsonc` var
 *   `R2_PUBLIC_URL` — onto `process.env` BEFORE the request handler runs. So an
 *   SSR render inside a request sees the CDN base here exactly as it does via
 *   `getCloudflareContext()`. (That was not true when `lib/r2.ts`'s doc-block
 *   was written, which is why the context fallback was added; it is now
 *   belt-and-braces and stays in `lib/r2.ts`.)
 * - **`next dev` / Node test runs:** `.env.local` populates `process.env` — the
 *   path `lib/r2.ts` already prefers.
 * - **The browser:** the var is a Worker `var`, not `NEXT_PUBLIC_*`, so it is
 *   absent either way and a bare key falls back to the same-origin proxy. That
 *   pre-existing SSR-vs-browser difference is documented on
 *   `resolveContentImageUrl` in `lib/r2.ts`; splitting the module neither
 *   creates nor removes it.
 */

/** Strip a trailing slash so `${base}${path}` can never double up. */
export function normaliseR2Base(raw?: string | null): string {
  return raw ? raw.replace(/\/$/, "") : "";
}

/**
 * LOCAL-SNAPSHOT mode — `next dev` started with `USE_LOCAL_D1=1`
 * (`npm run dev`, and the pre-push gate's own phase-4/5 server).
 *
 * Mirrors `useLocalBinding` in `lib/r2-client.ts` EXACTLY, and it has to: that
 * flag already sends every R2 read/write to the local Miniflare bucket, but the
 * URL layer kept emitting `R2_PUBLIC_URL` (the PRODUCTION CDN,
 * `cdn.thefloralmuse.uk`) because `.env.local` sets it. So a local-mode page
 * served markup pointing at production bytes while the server itself read local
 * ones — the seeded snapshot was never actually exercised, and every storefront
 * image depended on an external host being reachable.
 *
 * That is what made the E2E image specs unfixable from inside this repo:
 * `playwright.config.ts` sets `use.extraHTTPHeaders["CF-Connecting-IP"]` on
 * EVERY request the browser makes — including the cross-origin ones to
 * `cdn.thefloralmuse.uk` — and Cloudflare answers a CDN request carrying that
 * header with **403** (measured 2026-08-16: same URL, 200 without the header,
 * 403 with it). Every plain `<img src="https://cdn…">` therefore rendered at
 * `naturalWidth === 0`. Images that happened to route through Next's own
 * optimiser survived, because that fetch is made by the dev SERVER, which sends
 * no such header — which is exactly why the failure looked arbitrary.
 *
 * Returning no base here makes every key resolve to the same-origin
 * `/api/gallery/image` proxy, which reads the LOCAL bucket — hermetic, and the
 * only configuration in which `npm run seed:local`'s media copy means anything.
 *
 * Production is untouched: the Worker runs `NODE_ENV === "production"`, so this
 * predicate is false there and `R2_PUBLIC_URL` still wins.
 */
export function usesLocalR2Snapshot(): boolean {
  return (
    process.env.NODE_ENV === "development" && process.env.USE_LOCAL_D1 === "1"
  );
}

/**
 * R2 public base URL from `process.env` only — no Cloudflare binding, so this is
 * safe in a client component. Empty string when unset (the browser, always) and
 * in LOCAL-SNAPSHOT mode (see `usesLocalR2Snapshot`).
 */
export function readR2BaseFromEnv(): string {
  if (usesLocalR2Snapshot()) return "";
  return normaliseR2Base(process.env.R2_PUBLIC_URL);
}

/**
 * Same-origin Worker proxy for an R2 key — the fallback when no CDN base is
 * configured (local dev without `R2_PUBLIC_URL`, and the browser, which never
 * receives the var because it is not `NEXT_PUBLIC_*`).
 */
export function galleryProxyUrl(key: string): string {
  return `/api/gallery/image?key=${encodeURIComponent(key)}`;
}

/** Join an R2 CDN base with a key/path. Empty string when there is no base. */
export function r2ImageUrlFromBase(base: string, key: string): string {
  if (!base) return "";
  const path = key.startsWith("/") ? key : `/${key}`;
  return `${base}${path}`;
}

/**
 * True for a reference that is ALREADY renderable as-is — an absolute URL or a
 * site-relative path (`/asset/…`) — and therefore needs no R2 base at all.
 */
export function isRenderableImageRef(key: string): boolean {
  return key.startsWith("http://") || key.startsWith("https://") || key.startsWith("/");
}

/**
 * Resolve a stored image reference to a renderable URL for a plain `<img>`,
 * against an explicit R2 CDN base. The shared core of both
 * `resolveContentImageUrl` implementations — see `lib/r2.ts` for the full
 * rationale on why a bare key prefers the CDN over the in-Worker proxy.
 */
export function resolveContentImageUrlFromBase(
  base: string,
  key?: string | null
): string | null {
  if (!key) return null;
  const k = key.trim();
  if (!k) return null;
  if (isRenderableImageRef(k)) return k;
  return r2ImageUrlFromBase(base, k) || galleryProxyUrl(k);
}

/**
 * Client-safe `resolveContentImageUrl`. Identical to `lib/r2.ts`'s export on
 * every runtime we ship (see the module doc-block), but reachable from a
 * `"use client"` component without pulling in the Cloudflare adapter.
 */
export function resolveContentImageUrl(key?: string | null): string | null {
  return resolveContentImageUrlFromBase(readR2BaseFromEnv(), key);
}
